export default {
  title: "My Account",
  subtitle: "Manage your profile and settings",
  description: "Manage your account settings",
  profile: {
    title: "Profile",
    completeProfile: "Complete Profile",
    defaultName: "Guest",
    noProfile: "No profile",
    errors: {
      loadProfile: "Error loading profile",
      saveProfile: "Error saving profile"
    },
    weddingInfo: {
      title: "Wedding Information",
      partner1: "Partner 1",
      partner2: "Partner 2",
      date: "Date",
      location: "Location",
      expectedGuests: "Expected Guests",
      notes: "Notes"
    }
  },
  settings: {
    title: "Settings",
    loadError: "Error loading settings",
    saveError: "Error saving settings",
    saveSuccess: "Settings saved successfully"
  },
  security: "Security",
  notifications: "Notifications",
  profilePhoto: "Profile Photo",
  changePhoto: "Change Photo",
  photoRequirements: "JPG, PNG or GIF, 2MB max",
  deletePhoto: "Delete Photo",
  saveChanges: "Save Changes",
  subscription: {
    title: "Subscription",
    currentPlan: "Current plan",
    free: "Free",
    essential: "Essential",
    pro: "Pro",
    manage: "Manage subscription",
    viewPlans: "View plans",
    upgrade: "Upgrade",
    eventsUsed: "Events used",
    limits: {
      maxGuests: "Max guests",
      maxTasks: "Max tasks",
      maxVendors: "Max vendors",
      maxEvents: "Max events"
    },
    features: {
      unlimitedEvents: "Unlimited events",
      unlimitedGuests: "Unlimited guests",
      export: "PDF & Excel Export",
      budget: "Budget & Analytics",
      seating: "Seating Plan",
      support: "Priority Support"
    },
    plans: {
      free: {
        name: "Free"
      },
      essential: {
        name: "Essential"
      },
      premium: {
        name: "Premium"
      },
      pro: {
        name: "Pro"
      }
    }
  },
  limits: {
    title: "Limits",
    maxGuests: "Maximum guests",
    maxTasks: "Maximum tasks",
    maxVendors: "Maximum vendors",
    maxCollaborators: "Maximum collaborators"
  },
  form: {
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email",
    phone: "Phone",
    address: "Address",
    city: "City",
    country: "Country",
    timezone: "Timezone",
    language: "Language",
    avatar: "Avatar"
  },
  actions: {
    save: "Save",
    cancel: "Cancel",
    edit: "Edit",
    delete: "Delete Account",
    changePassword: "Change Password",
    uploadAvatar: "Upload Avatar"
  },
  messages: {
    profileUpdated: "Profile updated successfully",
    passwordChanged: "Password changed successfully",
    avatarUploaded: "Avatar uploaded successfully",
    accountDeleted: "Account deleted successfully",
    error: "An error occurred"
  },
  events: {
    yourEvents: "Your events"
  },
  partners: {
    title: "Partners information",
    partner1: "Partner 1",
    partner2: "Partner 2",
    firstName: "First name",
    lastName: "Last name",
    email: "Email",
    save: "Save"
  },
  pageTitle: "Account | WedBox",
  accountManagement: "Account management",
  chooseWhatToManage: "Choose what you want to manage",
  myProfile: "My Profile",
  personalPreferences: "Personal preferences",
  managePersonalInfo: "Manage your personal information, profile photo, language and notification preferences.",
  accessMyProfile: "Access my profile",
  eventSettings: "Event settings",
  eventTypeSpecific: {
    wedding: {
      subtitle: "Specific to your wedding",
      description: "Manage your wedding details, partners, date, collaborators and statistics.",
      buttonText: "Wedding settings"
    },
    birthday: {
      subtitle: "Specific to your birthday",
      description: "Manage your birthday details, date, collaborators and statistics.",
      buttonText: "Birthday settings"
    },
    "baby-shower": {
      subtitle: "Specific to your baby shower",
      description: "Manage your baby shower details, date, collaborators and statistics.",
      buttonText: "Baby shower settings"
    },
    baptism: {
      subtitle: "Specific to your baptism",
      description: "Manage your baptism details, date, collaborators and statistics.",
      buttonText: "Baptism settings"
    },
    engagement: {
      subtitle: "Specific to your engagement",
      description: "Manage your engagement details, date, collaborators and statistics.",
      buttonText: "Engagement settings"
    },
    default: {
      subtitle: "Specific to your event",
      description: "Manage your event details, date, collaborators and statistics.",
      buttonText: "Event settings"
    }
  },
  improvedOrganization: "📋 Improved organization",
  organizationNote: "We have separated personal settings from event-specific settings for better organization. Your user preferences are now in \"My Profile\", while everything about your event is in \"Event Settings\".",
  subscriptionManagement: "Subscription Management",
  manageYourPlan: "Manage your plan and track your usage",
  displayName: "Display name",
  phoneNumber: "Phone number",
  yourFullName: "Your full name",
  emailAddress: "Email address",
  emailCannotBeModified: "Email cannot be modified for security reasons",
  personalInformation: "Personal information",
  languageInterface: "Interface language",
  notificationPreferences: "Notification preferences",
  emailNotifications: "Email notifications",
  receiveEmailNotifications: "Receive email notifications",
  pushNotifications: "Push notifications",
  receiveBrowserNotifications: "Receive browser notifications",
  deleteAccountWarning: "⚠️ This action is irreversible. All your data will be permanently deleted.",
  permanentlyDeleteAccount: "Permanently delete my account",
  languageChangedTo: "Language changed to {{language}}",
  error: "Error:",
  saveError: "You can add an error notification here",
  language: "Language",
  languageDescription: "Choose the display language for the application",
  languageSelect: "Select your language",
  weddingDetails: "Wedding Details",
  weddingDate: "Wedding Date",
  partnerInfo: "Partner Information",
  partner1: "Partner 1",
  partner2: "Partner 2",
  firstName: "First Name",
  lastName: "Last Name",
  accessManagement: "Access Management",
  accessManagementDescription: "Manage who can access your wedding data",
  manageCollaborators: "Manage Collaborators",
  loadError: "Error loading settings",
  saveSuccess: "Settings saved successfully",
  noData: "No data available",
  tabs: {
    profile: "Profile",
    sharing: "Sharing",
    settings: "Settings"
  },
  delete: {
    title: "Delete Account",
    description: "Permanently delete your account and all associated data",
    confirmTitle: "Are you sure?",
    confirmDescription: "This action cannot be undone. All your data will be permanently deleted.",
    confirmButton: "Delete Account",
    gdprNotice: "In accordance with GDPR, all your personal data will be permanently deleted."
  },
  deleteAccount: {
    title: "Delete Account",
    description: "Permanently delete your account and all associated data",
    warning: "This action is irreversible",
    confirm: "Confirm deletion",
    confirmDescription: "Please confirm your password to continue",
    confirmFacebookDescription: "Please confirm with Facebook to continue",
    confirmGoogleDescription: "Please confirm with Google to continue",
    passwordLabel: "Password",
    reasonLabel: "Reason for deletion",
    feedbackLabel: "Feedback (optional)",
    errors: {
      authCancelled: "Authentication cancelled",
      generic: "An error occurred",
      recentLogin: "Please log in again to perform this action",
      wrongPassword: "Incorrect password",
      invalidPassword: "Invalid password",
      popupClosed: "Window was closed",
      reasonRequired: "Reason is required"
    },
    success: "Account deleted successfully",
    reasons: {
      select: "Select a reason",
      foundAlternative: "Found an alternative",
      tooComplex: "Too complex",
      notUseful: "Not useful for me",
      other: "Other"
    }
  },
  export: {
    title: "Export Data",
    description: "Download all your data in JSON format",
    button: "Download Data",
    loading: "Preparing download...",
    gdprNotice: "This export includes all your personal data as per GDPR requirements."
  },
  errors: {
    deleteAccount: "Error deleting account",
    exportData: "Error exporting data"
  },
  collaborators: {
    title: "Collaborators",
    description: "Manage people who can help with your wedding planning",
    count: "{{count}} collaborators",
    invite: "Invite collaborator",
    inviteEmail: "Email address",
    placeholder: "Enter email address",
    role: "Role",
    roles: {
      admin: "Administrator",
      editor: "Editor",
      viewer: "Viewer"
    },
    roleDescriptions: {
      admin: "Can modify everything and manage collaborators",
      editor: "Can modify selected sections",
      viewer: "Can only view selected sections"
    },
    rolesTitle: "Role Management",
    accessTo: "Access to",
    accessToSections: "Access to sections",
    resourceTypes: {
      all: "All",
      tasks: "Tasks",
      guests: "Guests",
      budget: "Budget",
      timeline: "Timeline",
      tasks_and_guests: "Tasks and Guests"
    },
    status: {
      pending: "Pending",
      accepted: "Accepted",
      refused: "Refused",
      expired: "Expired"
    },
    lastActive: "Last active",
    remove: "Remove collaborator",
    modifyAccess: "Modify access",
    updateRoleError: "Error updating role",
    inviteSuccess: "Invitation sent successfully",
    inviteError: "Error sending invitation",
    list: "Collaborator list",
    empty: "No collaborators yet"
  },
  sharing: {
    activeLinksTitle: "Active Sharing Links",
    activeLinksDesc: "These links allow access to your event without a user account.",
    accessTo: "Access to"
  }
};
